import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Readonly<Record<ButtonVariant, string>> = {
  primary:
    'bg-accent text-accent-fg shadow-sm hover:bg-accent-hover active:bg-accent-active border border-transparent',
  secondary:
    'bg-surface text-fg border border-border-strong hover:bg-surface-hover active:bg-surface-sunken',
  ghost: 'bg-transparent text-fg-muted border border-transparent hover:bg-surface-hover hover:text-fg',
  danger:
    'bg-[var(--color-danger)] text-[var(--color-text-inverse)] border border-transparent hover:opacity-90',
  link: 'bg-transparent border border-transparent text-accent underline underline-offset-2 hover:text-accent-hover px-0',
};

const SIZE_CLASSES: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-8 px-2.5 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /**
   * Shows a spinner and disables the control. The accessible name is unchanged and
   * aria-busy is set, so a screen reader hears "busy" rather than a label that suddenly
   * says something different.
   */
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, fullWidth = false, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center rounded-[var(--radius-control)] font-medium',
        'transition-colors duration-100',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});
